import { type APIFieldAttributes, type ArdoqId } from '@ardoq/api-types';
import { isNil } from 'lodash';
import { modelTypeKey } from './modelUtils';
import { isTruthy } from '@ardoq/common-helpers';
import {
  ComponentTypeInfo,
  RawGraphEdge2,
  ReferenceTypeInfo,
  type UrlFieldValue,
} from '../types';
import { GraphInterface } from '../graphInterface/graphInterface';
import { LabelsInterface } from '../labelsInterface/labelsInterface';
import { fieldOperations } from '@ardoq/core';

type Interface = GraphInterface & LabelsInterface;

export const getUrlFieldsByComponentType = (
  viewInterface: Interface,
  componentTypes: ComponentTypeInfo[]
) =>
  new Map(
    componentTypes.map(
      ({ componentType: { id: componentTypeId }, workspaceModelId }) => [
        modelTypeKey(componentTypeId, workspaceModelId),
        viewInterface
          .getFieldsByComponentType(componentTypeId, workspaceModelId)
          .filter(fieldOperations.isURLField),
      ]
    )
  );

interface GetFieldValueMapArgs {
  modelIds: ArdoqId[];
  getTypeId: (modelId: ArdoqId) => string | number | undefined | null;
  getWorkspaceId: (modelId: ArdoqId) => string | null;
  fieldsByType: Map<ArdoqId, APIFieldAttributes[]>;
  getFieldValue: (modelId: ArdoqId, fieldName: string) => any;
  getWorkspaceModelId: (workspaceId: ArdoqId) => ArdoqId | null;
}
const getFieldValueMap = ({
  modelIds,
  getTypeId,
  getWorkspaceId,
  fieldsByType,
  getFieldValue,
  getWorkspaceModelId,
}: GetFieldValueMapArgs) =>
  new Map(
    modelIds.map(modelId => {
      const typeId = getTypeId(modelId);
      if (isNil(typeId)) {
        return [modelId, null];
      }

      const workspaceId = getWorkspaceId(modelId);
      if (!workspaceId) {
        return [modelId, null];
      }

      const workspaceModelId = getWorkspaceModelId(workspaceId) ?? '';

      const urlFields = fieldsByType.get(
        modelTypeKey(typeId, workspaceModelId)
      );

      if (!urlFields) {
        return [modelId, null];
      }
      const urlFieldValues = urlFields
        .map(({ name: urlFieldName, label }) => {
          const url = getFieldValue(modelId, urlFieldName);
          if (!url) {
            return null;
          }
          return {
            label,
            url,
          };
        })
        .filter(isTruthy);
      return [modelId, urlFieldValues.length ? urlFieldValues : null];
    })
  );

export const componentUrlFieldValues = (
  viewInterface: Interface,
  componentIds: ArdoqId[],
  urlFieldsByComponentType: Map<ArdoqId, APIFieldAttributes[]>
) =>
  getFieldValueMap({
    modelIds: componentIds,
    getTypeId: viewInterface.getComponentTypeId,
    getWorkspaceId: viewInterface.getComponentWorkspaceId,
    fieldsByType: urlFieldsByComponentType,
    getFieldValue: viewInterface.getComponentFieldValue,
    getWorkspaceModelId: viewInterface.getWorkspaceModelId,
  });

const componentIdToTypeMapEntry = (
  viewInterface: Interface,
  componentId: ArdoqId
) => {
  const componentType = viewInterface.getComponentType(componentId);
  const workspaceId = viewInterface.getComponentWorkspaceId(componentId);
  if (!componentType || !workspaceId) return null;

  const workspaceModelId = viewInterface.getWorkspaceModelId(workspaceId);
  if (!workspaceModelId) return null;

  const key = modelTypeKey(componentType.id, workspaceModelId);
  return [key, { componentType, workspaceModelId }] as const;
};
const getComponentTypesFromViewInterface = (
  viewInterface: Interface,
  componentIds: ArdoqId[]
): ComponentTypeInfo[] => {
  return Array.from(
    new Map(
      componentIds
        .map(componentId =>
          componentIdToTypeMapEntry(viewInterface, componentId)
        )
        .filter(isTruthy)
    ).values()
  );
};

export const getUrlFieldValuesByComponentId = (
  viewInterface: Interface,
  componentIds: ArdoqId[]
) => {
  const componentTypes = getComponentTypesFromViewInterface(
    viewInterface,
    componentIds
  );
  const urlFieldsByComponentType = getUrlFieldsByComponentType(
    viewInterface,
    componentTypes
  );
  return componentUrlFieldValues(
    viewInterface,
    componentIds,
    urlFieldsByComponentType
  );
};

export const getUrlFieldsByReferenceType = (
  viewInterface: Interface,
  referenceTypes: ReferenceTypeInfo[]
) =>
  new Map(
    referenceTypes.map(({ typeId, workspaceModelId }) => [
      modelTypeKey(typeId ?? '', workspaceModelId),
      viewInterface
        .getFieldsByReferenceType(typeId, workspaceModelId)
        .filter(fieldOperations.isURLField),
    ])
  );
export const referenceUrlFieldValues = (
  viewInterface: Interface,
  referenceIds: ArdoqId[],
  urlFieldsByReferenceType: Map<ArdoqId, APIFieldAttributes[]>
) =>
  getFieldValueMap({
    modelIds: referenceIds,
    getTypeId: viewInterface.getReferenceTypeId,
    getWorkspaceId: viewInterface.getReferenceRootWorkspaceId,
    fieldsByType: urlFieldsByReferenceType,
    getFieldValue: viewInterface.getReferenceFieldValue,
    getWorkspaceModelId: viewInterface.getWorkspaceModelId,
  });

const referenceIdToTypeMapEntry = (
  viewInterface: Interface,
  referenceId: ArdoqId
) => {
  const typeId = viewInterface.getReferenceTypeId(referenceId);
  const workspaceId = viewInterface.getReferenceRootWorkspaceId(referenceId);

  if (isNil(typeId) || !workspaceId) {
    return null;
  }

  const referenceType = viewInterface.getReferenceTypeById(workspaceId, typeId);

  if (!referenceType) {
    return null;
  }
  const workspaceModelId = viewInterface.getWorkspaceModelId(workspaceId);
  if (!workspaceModelId) return null;

  const key = modelTypeKey(typeId, workspaceModelId);
  return [key, { referenceType, typeId, workspaceModelId }] as const;
};

const getReferenceTypesFromViewInterface = (
  viewInterface: Interface,
  referenceIds: ArdoqId[]
): ReferenceTypeInfo[] => {
  return Array.from(
    new Map(
      referenceIds
        .map(referenceId =>
          referenceIdToTypeMapEntry(viewInterface, referenceId)
        )
        .filter(isTruthy)
    ).values()
  );
};

/**
 * @returns an array of all reference ids in the list, including the aggregated references
 */
export const getReferenceIdsFromReferenceList = (
  rawReferences: RawGraphEdge2[]
) =>
  rawReferences
    .filter(({ isParentChildReference }) => !isParentChildReference)
    .flatMap(({ modelId, modelIds }) =>
      modelIds ? modelIds : modelId ? [modelId] : []
    );

export const getUrlFieldValuesByReferenceId = (
  viewInterface: Interface,
  referenceIds: ArdoqId[]
) => {
  const referenceTypes = getReferenceTypesFromViewInterface(
    viewInterface,
    referenceIds
  );
  const urlFieldsByReferenceType = getUrlFieldsByReferenceType(
    viewInterface,
    referenceTypes
  );
  return referenceUrlFieldValues(
    viewInterface,
    referenceIds,
    urlFieldsByReferenceType
  );
};

export const getNodeUrlFieldValues = (
  modelId: ArdoqId | null | undefined,
  referenceIds: ArdoqId[] | null | undefined,
  urlFieldValuesByComponentId: Map<ArdoqId, UrlFieldValue[] | null>,
  urlFieldValuesByReferenceId: Map<ArdoqId, UrlFieldValue[] | null>
): UrlFieldValue[] => {
  const componentUrlFieldValues = modelId
    ? (urlFieldValuesByComponentId.get(modelId) ?? [])
    : [];
  return [
    ...componentUrlFieldValues.map(urlFieldValue => ({
      ...urlFieldValue,
      isReference: false,
    })),
    ...(referenceIds ?? []).flatMap(referenceId =>
      (urlFieldValuesByReferenceId.get(referenceId) ?? []).map(
        urlFieldValue => ({ ...urlFieldValue, isReference: true })
      )
    ),
  ];
};
